import Button from '@berlitz/button'
import Spacer from '@berlitz/spacer'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import enGB from 'date-fns/locale/en-GB'
import { ArrayHelpers, Field, FieldArray, FieldProps, Formik, FormikErrors, FormikProps } from 'formik'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Col, Row } from 'react-styled-flexboxgrid'

import MultiSelect from '@berlitzplatforms/micro.ui.multi-select-legacy'
import PortalInstructor from '@classes/Instructor'
import Checkbox from '@components/Checkbox'
import Datepicker from '@components/Datepicker'
import { RepeatOnOptions, TypeOptions } from '@components/Modal/AvailabilityModal/constants'
import { StoreProps } from '@interfaces/StoreState'
import { DATE_FORMAT } from '@utils/constants'
import { cloneDeep, isEmpty } from 'lodash'
import { useSelector } from 'react-redux'
import { FormProps, FormValueProps } from './interface'
import { Footer, Label, StyledForm } from './style'

const AvailabilityBulkDeleteForm: React.FC<FormProps & WithUserTimeProps> = ({
  onClose,
  onDelete,
  initialValues,
  toDate = () => new Date(),
  currentDate = moment(),
}) => {
  const [startDate, setStartDate] = useState<string | undefined>()
  const [endDate, setEndDate] = useState<string | undefined>()
  const [checkAll, setCheckAll] = useState(false)
  const [formValues] = useState<FormValueProps>(cloneDeep(initialValues))

  const profile = useSelector(({ userProfile }: StoreProps) => userProfile?.info)
  const Instructor = new PortalInstructor(profile.InstructorProfile.instructorInfo)

  const intl = useIntl()

  useEffect(() => {
    setStartDate(formValues?.StartDate?.clone().format(DATE_FORMAT.DATEPICKER))
  }, [])

  const changeDate = useCallback((cb) => cb(), [])
  const debouceRequest = useCallback((cb) => changeDate(cb), [])
  const isInvalidDates = () => {
    const mStart = moment(startDate, DATE_FORMAT.DATEPICKER, true)
    const mEnd = moment(endDate, DATE_FORMAT.DATEPICKER, true)
    return (
      !mStart.isValid() || !mEnd.isValid() || mStart.isAfter(mEnd) || mStart.isBefore(Instructor.CurrentDate, 'day')
    )
  }

  const minDate = toDate(currentDate)

  const isDateValid = (date: string = '') => date.match(/^(0?[1-9]|[12][0-9]|3[01])[\/](0?[1-9]|1[012])[\/]\d{4}$/)
  return (
    <Formik
      initialValues={formValues}
      enableReinitialize
      onSubmit={(values) => {
        onDelete(values)
      }}
      validate={(values: FormValueProps) => {
        const errors: FormikErrors<FormValueProps> = {}

        if (!values.RepeatOn.length) {
          RepeatOnOptions.map((_date, index) => {
            errors[`RepeatOn.${index}`] = 'Required'
          })
        }

        if (!values.Type) {
          errors.Type = 'Required'
        }

        return errors
      }}
      render={({ errors, values, isValid, dirty, handleSubmit }: FormikProps<FormValueProps>) => (
        <StyledForm onSubmit={handleSubmit}>
          <Row>
            <Col xs={6} md={3}>
              <Field
                name="StartDate"
                render={({ form, field }: FieldProps<FormValueProps>) => (
                  <Datepicker
                    locale={enGB}
                    value={startDate}
                    selected={toDate(values.StartDate.clone())}
                    dateFormat="dd/MM/yyyy"
                    minDate={minDate}
                    maxDate={values.EndDate ? toDate(values.EndDate) : undefined}
                    label={intl.formatMessage({ id: 'Start date' })}
                    openToDate={toDate(values.StartDate)}
                    onSelect={(val, event) => {
                      const d = moment(val)

                      if (val && d.isValid() && event.type === 'click') {
                        form.setFieldValue(field.name, d)
                        setStartDate(d.format(DATE_FORMAT.DATEPICKER))
                      } else {
                        form.setFieldError(field.name, 'Required')
                      }
                    }}
                    onChangeRaw={(e) => {
                      debouceRequest(() => {
                        const value = e.currentTarget.value || ''
                        setStartDate(value)

                        if (isDateValid(value)) {
                          const d = moment(value, DATE_FORMAT.DATEPICKER)
                          form.setFieldValue(field.name, d)
                          setStartDate(d.format(DATE_FORMAT.DATEPICKER))
                        } else {
                          form.setFieldError(field.name, 'Required')
                        }
                      })
                    }}
                    isClearable={false}
                  />
                )}
              />
            </Col>

            <Col xs={6} md={3}>
              <Field
                name="EndDate"
                render={({ form, field }: FieldProps<FormValueProps>) => (
                  <Datepicker
                    locale={enGB}
                    value={endDate}
                    dateFormat="dd/MM/yyyy"
                    minDate={toDate(values.StartDate.clone())}
                    label={`${intl.formatMessage({ id: 'End date' })}*`}
                    openToDate={toDate(values.EndDate)}
                    onSelect={(val, event) => {
                      const d = moment(val)

                      if (val && d.isValid() && event.type === 'click') {
                        form.setFieldValue(field.name, d)
                        setEndDate(d.format(DATE_FORMAT.DATEPICKER))
                      } else {
                        form.setFieldError(field.name, 'Required')
                      }
                    }}
                    onChangeRaw={(e) => {
                      debouceRequest(() => {
                        const value = e.currentTarget.value || ''
                        setEndDate(value)

                        if (isDateValid(value)) {
                          const d = moment(e.currentTarget.value, DATE_FORMAT.DATEPICKER)
                          form.setFieldValue(field.name, d)
                          setEndDate(d.format(DATE_FORMAT.DATEPICKER))
                        } else {
                          form.setFieldError(field.name, 'Required')
                        }
                      })
                    }}
                    isClearable={false}
                  />
                )}
              />
            </Col>

            <Col xs={6} md={3}>
              <Field
                name="Type"
                render={({ form, field }: FieldProps<FormValueProps>) => (
                  <>
                    <Label>
                      <FormattedMessage id="Type" />
                    </Label>
                    <div id="type">
                      <MultiSelect
                        options={TypeOptions}
                        onChange={(value) => {
                          form.setFieldValue(field.name, value)
                        }}
                        placeholder={intl.formatMessage({ id: 'Select', defaultMessage: 'Select' })}
                        selectAllLabel={intl.formatMessage({ id: 'All types', defaultMessage: 'All types' })}
                      ></MultiSelect>
                    </div>
                  </>
                )}
              />
            </Col>
            <Col xs={12} lg={false}>
              <Spacer size="md" />
            </Col>
          </Row>

          <Spacer size="xs" />

          <FieldArray
            name="RepeatOn"
            render={(arrayHelpers: ArrayHelpers) => (
              <>
                <Label>
                  <FormattedMessage id="Delete time off on these days:" />
                </Label>

                <Row>
                  <Col xs={12} md={3}>
                    <Checkbox
                      checked={checkAll}
                      name="check-all"
                      label={intl.formatMessage({ id: 'All' })}
                      value="All"
                      field={{
                        name: intl.formatMessage({ id: 'All' }),
                      }}
                      onChange={(e: any) => {
                        const { insert, remove, pop } = arrayHelpers
                        setCheckAll(e.target.checked)
                        values.RepeatOn.map((_val, index) => {
                          remove(index)
                        })

                        RepeatOnOptions.map((day, index) => {
                          if (e.target.checked) {
                            insert(index, day.value)
                          } else {
                            pop()
                          }
                        })
                      }}
                    />
                    <Spacer size="xxs" />
                  </Col>
                  {RepeatOnOptions.map((day, index) => (
                    <Col xs={12} md={3} key={`repeat-on-${index}`}>
                      <Checkbox
                        key={`repeat-on-${index}`}
                        checked={values.RepeatOn.includes(day.value)}
                        name={`RepeatOn.${index}`}
                        label={intl.formatMessage({ id: day.label })}
                        value={`${day.value}`}
                        field={{
                          name: day.label,
                        }}
                        onChange={(e: any) => {
                          const { insert, remove } = arrayHelpers
                          if (e.target.checked) {
                            setCheckAll(values.RepeatOn.length + 1 === RepeatOnOptions.length)
                            insert(index, day.value)
                          } else {
                            const valueIndex = values.RepeatOn.indexOf(day.value)
                            setCheckAll(values.RepeatOn.length - 1 === RepeatOnOptions.length)
                            remove(valueIndex)
                          }
                        }}
                      />
                      <Spacer size="xxs" />
                    </Col>
                  ))}
                </Row>
              </>
            )}
          />

          <Spacer size="xxxl" />

          <Footer>
            <Button type="button" onClick={onClose}>
              <FormattedMessage id="Cancel" />
            </Button>

            <Button
              type="button"
              buttonType="error"
              onClick={() => onDelete(values)}
              disabled={!isValid || !dirty || !isEmpty(errors) || isInvalidDates()}
            >
              <FormattedMessage id="Delete" />
            </Button>
          </Footer>
        </StyledForm>
      )}
    />
  )
}

export default WithUserTimeHOC(AvailabilityBulkDeleteForm)
